import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { roundMoney } from './money-round'   // 1.53 — the tetri the database keeps

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Alphabetical by the GEORGIAN alphabet, for any list a shopkeeper reads.
 *
 * Not a plain string compare: `ს` must come after `ბ` by the Georgian
 * alphabet, not by Unicode code point. `numeric: true` keeps
 * "ბაკურიანი 1.5" after "ბაკურიანი 1.0" instead of ordering the digits
 * character by character.
 *
 * Categories arrive from the database in creation order, which only means
 * something to whoever typed them. Every list that shows them to a person
 * sorts with this, so the till and the warehouse never disagree.
 */
export const byGeorgianName = <T extends { name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name, 'ka', { numeric: true, sensitivity: 'base' })

/**
 * Weighted average cost after receiving `boughtQty` units at `boughtCost`.
 *
 * Mirrors `create_purchase` (migration 034, unchanged since) EXACTLY:
 *
 *   purchase_price = CASE
 *     WHEN quantity > 0 AND quantity + v_qty > 0
 *       THEN ROUND((quantity * purchase_price + v_qty * v_cost) / (quantity + v_qty), 2)
 *     ELSE v_cost
 *   END
 *
 * `heldQty` MUST be the org-wide quantity — the same number the DB column
 * holds, which trigger A keeps equal to SUM(product_stock) across branches.
 * Passing the active branch's slice instead over-weights the incoming price:
 * receiving 20 units at ₾2.00 against 18 org-wide units at ₾1.20 gives ₾1.62,
 * but against the 1 unit sitting in the branch you are standing in it gives
 * ₾1.96 — 21% high, and margin, profit and below-cost warnings all inherit it.
 *
 * Cost is an org-wide property of a product. Stock is per branch. This function
 * exists so that distinction has one place to be stated and one place to break.
 */
export function blendCost(
  heldQty: number,
  heldCost: number,
  boughtQty: number,
  boughtCost: number,
): number {
  const total = heldQty + boughtQty
  if (!(heldQty > 0) || !(total > 0)) return boughtCost
  return Math.round(((heldQty * heldCost + boughtQty * boughtCost) / total) * 100) / 100
}

/**
 * Money for the screen, with the minus in front of the currency symbol.
 *
 * `₾${v.toFixed(2)}` renders a loss as „₾-818.50" — technically signed, but the
 * symbol and the sign are the wrong way round and it reads as a stray dash. The
 * P&L had a worse version of the same problem: `Math.abs()` dropped the sign
 * entirely and a ₾818.50 LOSS was printed as „₾818.50", in red. Red is not a
 * minus sign; a shopkeeper reads the digits.
 *
 * One function so every screen shows the same number the same way.
 */
export const money = (v: number) => (Number.isFinite(v) ? `${v < 0 ? '−' : ''}₾${roundMoney(Math.abs(v)).toFixed(2)}` : `₾${v}`)
